import { useEffect, useLayoutEffect, useRef, useState } from 'react';

/**
 * Column Chooser popup for <AppDataGrid> ("AppDataGrid global grid features"
 * task — Hide / Show-all / Chooser). A checkbox per column toggles its
 * visibility; the last remaining visible column can't be unchecked (a grid
 * always shows ≥1 column). Reuses the shared `.cell-ctx-menu` styling and the
 * SAME transient-popup rules as the header menu / filter popups (§18.6): clamp
 * inside the viewport, close on outside-click / Escape / scroll.
 */
export type ChooserColumn = { field: string; label: string };

export function ColumnChooser({
  columns,
  hidden,
  position,
  onToggle,
  onShowAll,
  onClose,
}: {
  columns: ChooserColumn[];
  hidden: Set<string>;
  position: { x: number; y: number };
  onToggle: (field: string) => void;
  onShowAll: () => void;
  onClose: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState(position);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    let { x, y } = position;
    if (x + r.width > window.innerWidth - 8) x = Math.max(8, window.innerWidth - r.width - 8);
    if (y + r.height > window.innerHeight - 8) y = Math.max(8, window.innerHeight - r.height - 8);
    setPos({ x, y });
  }, [position]);

  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    // Capture phase + handled: Esc closes ONLY this menu. On the document it ran
    // after the hotkey store, whose Esc = the form's Cancel (closed the form
    // behind — Open Quote, 2026-10-20).
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.preventDefault(); onClose(); } };
    // Close only on a GRID/PAGE scroll (which would move the anchor) — NOT when
    // the user scrolls the chooser's OWN column list (that must scroll, not close).
    const onScroll = (e: Event) => {
      if (ref.current && ref.current.contains(e.target as Node)) return;
      onClose();
    };
    document.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onKey, true);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('keydown', onKey, true);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [onClose]);

  const visibleCount = columns.filter((c) => !hidden.has(c.field)).length;

  return (
    <div
      ref={ref}
      className="cell-ctx-menu adg-chooser"
      role="menu"
      style={{ left: pos.x, top: pos.y }}
      onContextMenu={(e) => e.preventDefault()}
    >
      <div className="adg-chooser-head">
        <i className="ti ti-columns" /> Columns
      </div>
      <div className="adg-chooser-list">
        {columns.map((c) => {
          const visible = !hidden.has(c.field);
          const isLastVisible = visible && visibleCount <= 1; // can't hide the last one
          return (
            <label key={c.field} className={`adg-chooser-item${isLastVisible ? ' disabled' : ''}`}>
              <input
                type="checkbox"
                checked={visible}
                disabled={isLastVisible}
                onChange={() => onToggle(c.field)}
              />
              <span>{c.label}</span>
            </label>
          );
        })}
      </div>
      <button
        type="button"
        className="adg-chooser-showall"
        disabled={hidden.size === 0}
        onClick={() => onShowAll()}
      >
        <i className="ti ti-eye" /> Show all columns
      </button>
    </div>
  );
}
